import { DateTime } from 'luxon';
import { useCallback } from 'react';

import ProductPropertyType from '../../types/ProductPropertyType';
import { roundWithPrecision } from '../../util/number';
import SchemaType from "../../types/seo/SchemaType";
import globalTranslations from '../../i18n/globalTranslations';

const supportedSchemaProperties = [
  ProductPropertyType.SIZE,
  ProductPropertyType.COLOR,
];

const useProductSchema = ({
  accountStore,
  configStore,
  currencyStore,
  countryStore,
  routeService,
  locationContext,
  intl,
}) => {
  const getProductSchema = useCallback((product, activeProductId) => {
    const baseUrl = `${locationContext.protocol}//${locationContext.host}`;
    const productUrl = `${baseUrl}${routeService.getProductPath(product)}`;
    const withTax = accountStore.showPricesWithTax;
    const priceCurrency = currencyStore.currencyCode;
    const activeCountry = countryStore.activeCountry;

    const shouldShowModel = () => {
      return configStore.product.showModel;
    };

    const getProductSchemaName = () => {
      if (shouldShowModel() && product.model) {
        return `${product.name} ${product.model}`;
      }

      return product.name;
    };

    const getImageUrls = () => {
      return product.getImagesBySize('full').map((imagePathOrUrl) => {
        if (imagePathOrUrl.includes('http')) {
          return imagePathOrUrl;
        }

        return `${baseUrl}${imagePathOrUrl}`;
      });
    };

    const productSchema = {
      '@type': SchemaType.PRODUCT,
      name: getProductSchemaName(),
      image: getImageUrls(),
      sku: product.id,
      ...(product.ean && { gtin: product.ean }),
      ...(product.description_short || product.seoDescription
        ? { description: product.description_short || product.seoDescription }
        : {}),
      ...(product.manufacturer_product_id && { mpn: product.manufacturer_product_id }),
      ...(shouldShowModel() && { model: product.model }),
    };

    const getShippingDetails = () => {
      const shippingDetails = product.shippingDetails?.get(activeProductId);

      if (shippingDetails) {
        return shippingDetails.options
          .map((shippingOption) => {
            if (!shippingOption.delivery_time) {
              return;
            }

            const { handlingTime } = shippingOption.getAnalyticsDeliveryTimes({
              processingTime: product.processing_time,
            });
            const { transitTime } = shippingOption.getAnalyticsDeliveryTimes({
              deliveryTime: shippingOption.delivery_time,
            });

            return {
              '@type': SchemaType.OFFER_SHIPPING_DETAILS,
              deliveryTime: {
                '@type': SchemaType.SHIPPING_DELIVERY_TIME,
                handlingTime: {
                  '@type': SchemaType.QUANTITATIVE_VALUE,
                  minValue: handlingTime.minValue,
                  maxValue: handlingTime.maxValue,
                  unitCode: handlingTime.unitCode,
                },
                transitTime: {
                  '@type': SchemaType.QUANTITATIVE_VALUE,
                  minValue: transitTime.minValue,
                  maxValue: transitTime.maxValue,
                  unitCode: transitTime.unitCode,
                },
              },
              shippingDestination: {
                '@type': SchemaType.DEFINED_REGION,
                addressCountry: activeCountry.iso_code_2,
              },
              shippingRate: {
                '@type': SchemaType.MONETARY_AMOUNT,
                value: shippingOption.getPrice(withTax),
                currency: priceCurrency,
              },
            };
          })
          .filter((shippingDetails) => shippingDetails);
      }

      return [];
    };

    const getAvailability = () => {
      // Schema is always provided by the backend unless the product is not indexed
      return product.schema && 'https://schema.org/' + product.schema.availability;
    };

    const getValidProductPriceDate = () => {
      if (!product.price_info) {
        return null;
      }

      return DateTime.fromISO(product.price_info.valid_until).toISODate();
    };

    const getProductOfferSchema = () => {
      const calculatePrice = () => {
        let price = 0;

        if (product.price_info) {
          const roundedPriceWithPrecision = roundWithPrecision(
            product.price_info.getPrice(withTax),
            currencyStore.activeCurrencyPrecision
          );

          // Round price again by how many decimals are shown on product page
          price = roundWithPrecision(
            roundedPriceWithPrecision,
            currencyStore.activeCurrencyDecimalShown
          );
        }

        return price;
      };

      const shippingDetails = getShippingDetails();
      const validUntil = getValidProductPriceDate();
      const availability = getAvailability();
      const availabilityStarts = product.schema && product.schema.availabilityStarts;
      return {
        '@type': SchemaType.OFFER,
        priceCurrency,
        url: productUrl,
        price: calculatePrice(),
        ...(validUntil && { priceValidUntil: validUntil }),
        ...(availability && { availability }),
        ...(availabilityStarts && { availabilityStarts }),
        ...(shippingDetails && shippingDetails.length > 0 ? { shippingDetails } : {}),
      };
    };

    if (product.mainCategory) {
      productSchema.category = product.mainCategory.hierarchy
        .map((category) => category.name)
        .join('>');
    }

    if (configStore.productPage.showWeight) {
      productSchema.weight = {
        '@type': SchemaType.QUANTITATIVE_VALUE,
        value: product.display_weight,
        unitCode: product.stock_unit,
      };
    }

    productSchema.offers = getProductOfferSchema();

    return productSchema;
  }, []);

  const getProductGroupSchema = useCallback((product) => {
    const baseUrl = `${locationContext.protocol}//${locationContext.host}`;
    const url = `${baseUrl}${routeService.getProductPath(product)}`;

    const productGroupSchema = getBaseSchema(product);
    productGroupSchema['@type'] = SchemaType.PRODUCT_GROUP;
    productGroupSchema.name = product.multiproduct_title || product.name;
    productGroupSchema.productGroupID = product.id;
    productGroupSchema.url = url;

    const shouldShowModel = () => {
      return (
        configStore.productPage.showMultiProductModel &&
        configStore.product.showModel
      );
    };

    if (shouldShowModel()) {
      productGroupSchema.model = product.model;
    }

    const supportedProperties = {};
    product.multi.properties.forEach((property) => {
      if (supportedSchemaProperties.includes(property.type)) {
        supportedProperties[property.id] = property.type;
      }
    });

    const propertyValues = Object.values(supportedProperties);
    let hasPropertyVariant = false;

    const getProductVariants = () => {
      return product.multi.children.map((childProduct) => {
        const productProperties = {};

        const productSchema = getProductSchema(childProduct);

        productSchema.offers.url = productSchema.offers.url + childProduct.id;

        propertyValues.length && childProduct.schema && childProduct.schema.variesBy &&
        childProduct.schema.variesBy.forEach((property) => {
          hasPropertyVariant = true;
          productProperties[property.type] = property.value;
        });

        return {
          ...productSchema,
          ...productProperties,
        };
      });
    };

    productGroupSchema.hasVariant = getProductVariants();

    if (propertyValues.length && hasPropertyVariant) {
      productGroupSchema.variesBy = propertyValues.map(
        (property) => `https://schema.org/${property.toLowerCase()}`
      );
    }

    return productGroupSchema;
  }, [getProductSchema, configStore, routeService, locationContext]);

  const getBaseSchema = useCallback((product) => {
    const hideReviews = accountStore.isViewOnly;

    const baseSchema = {
      '@context': 'https://schema.org/',
      '@type': null,
      description: product.description_short || product.seoDescription,
    };

    if (product.manufacturer) {
      baseSchema.brand = {
        '@type': SchemaType.BRAND,
        name: product.manufacturer.name,
      };
    }

    const getProductReviewsSchema = () => {
      return product.reviews.map((review) => ({
        '@type': SchemaType.REVIEW,
        author: {
          '@type': SchemaType.PERSON,
          name: review.customers_name,
        },
        datePublished: DateTime.fromISO(review.date_added).toISODate(),
        reviewBody: review.description.reviews_text,
        reviewRating: [
          {
            '@type': SchemaType.RATING,
            ratingValue: review.reviews_rating,
            worstRating: 1,
            bestRating: 5,
            reviewAspect: intl.formatMessage(
              globalTranslations.reviewProductRatingTitle
            ),
          },
          {
            '@type': SchemaType.RATING,
            ratingValue: review.service_rating,
            worstRating: 1,
            bestRating: 5,
            reviewAspect: intl.formatMessage(
              globalTranslations.reviewServiceRatingTitle
            ),
          },
        ],
      }));
    };

    if (
      configStore.reviews.enabled &&
      !hideReviews &&
      !configStore.integrations.yotpo.enabled &&
      product.reviews.length
    ) {
      baseSchema.aggregateRating = {
        '@type': SchemaType.AGGREGATE_RATING,
        ratingValue: product.reviews_average,
        reviewCount: product.reviews_count,
      };

      baseSchema.review = getProductReviewsSchema();
    }

    return baseSchema;
  }, [accountStore, configStore, intl]);

  return [getProductSchema, getProductGroupSchema, getBaseSchema];
};

export default useProductSchema;
